import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowUp, Loader } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { AutoResizeTextarea } from '@/shared/components/auto-resize-textarea';
import { Button } from '@/shared/components/shadcn/button';
import {
  DateRangePicker,
  type DateRangeState,
} from '@/shared/components/shadcn/date-range-picker/date-range-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/shadcn/select';
import { useSavedQueries } from '@/shared/hooks/use-saved-queries';
import { useWorkspaceId } from '@/shared/hooks/use-workspace-id';
import { cn } from '@/shared/utils/cn';

// Form validation schema
const messageSchema = z.object({
  message: z
    .string()
    .min(1, 'Message cannot be empty')
    .max(1000, 'Message must be less than 1000 characters')
    .refine((value) => value.trim().length > 0, 'Message cannot be only whitespace'),
});

type MessageFormData = z.infer<typeof messageSchema>;

interface ChatInputProps {
  className?: string;
  onSubmit: (message: string) => void;
  loading?: boolean;
  hasPreviousAnswer: boolean;
  queryId: string | null;
  onQueryIdChange: (queryId: string) => void;
  dateRangeState: DateRangeState;
  onDateRangeStateChange: (dateRangeState: DateRangeState) => void;
}

export function ChatInput({
  className,
  onSubmit,
  loading,
  hasPreviousAnswer,
  queryId,
  onQueryIdChange,
  dateRangeState,
  onDateRangeStateChange,
}: ChatInputProps) {
  const { workspaceId } = useWorkspaceId();
  const { data: savedQueries } = useSavedQueries({
    workspaceId,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isValid },
  } = useForm<MessageFormData>({
    resolver: zodResolver(messageSchema),
    mode: 'onChange',
    defaultValues: {
      message: '',
    },
  });

  const messageValue = watch('message');
  const isFormDisabled = loading || !queryId;

  const onSubmitForm = (data: MessageFormData) => {
    if (!isFormDisabled && isValid) {
      onSubmit(data.message.trim());
      reset();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Don't submit when IME is composing (for Korean, Chinese, Japanese input)
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSubmit(onSubmitForm)();
    }
  };

  return (
    <div className={cn('bg-zinc-100 p-4 pt-0', className)}>
      <div className="rounded-md border border-zinc-200 bg-white">
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-3 px-4 py-3">
          <div className="flex items-center justify-between gap-1">
            <Select value={queryId || undefined} onValueChange={onQueryIdChange}>
              <SelectTrigger>
                <SelectValue placeholder="Select a query" />
              </SelectTrigger>
              <SelectContent>
                {savedQueries?.map((query) => (
                  <SelectItem key={query.id} value={query.id}>
                    {query.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DateRangePicker value={dateRangeState} onChange={onDateRangeStateChange} />
          </div>

          <div className="flex items-start gap-4">
            <div className="flex-1">
              <AutoResizeTextarea
                {...register('message')}
                onKeyDown={handleKeyDown}
                placeholder={hasPreviousAnswer ? 'Ask follow-up' : 'Ask anything'}
                disabled={isFormDisabled}
                className="border-0 bg-transparent p-0 text-base text-zinc-900 shadow-none focus-visible:ring-0"
                maxHeight={120}
                minHeight={40}
              />
            </div>
            <Button
              className="self-end"
              type="submit"
              size="icon"
              disabled={!messageValue?.trim() || !isValid || isFormDisabled}
            >
              {loading ? (
                <Loader className="size-4 animate-spin" />
              ) : (
                <ArrowUp className="size-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
